import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfileRestrictionsResponse } from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';

import RestrictionCardLayout from './restrictionCardLayout';

type PageState = DataFetchingState<AssociateProfileRestrictionsResponse>;

const DEFAULT_PAGE_STATE = {
  status: 'loading',
} as const;

const AssociateProfileRestrictions = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  useEffect(() => {
    const abortController = new AbortController();

    const getRestrictions = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        setPageState({ status: 'error' });
        return;
      }

      try {
        const { data } = await associateProfileService.getAssociateProfileRestrictions(alias, selectedSite.id, {
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted) {
          setPageState({
            status: 'success',
            data,
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch page data:', error);
          setPageState({ status: 'error' });
        }
      }
    };

    getRestrictions();

    return () => {
      abortController.abort();
    };
  }, [alias, selectedSite]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  if (
    pageState.status === 'success' &&
    pageState.data.pending.length === 0 &&
    pageState.data.active.length === 0 &&
    pageState.data.transferredIn.length === 0
  ) {
    return <Text>There were no restrictions found for this Associate</Text>;
  }

  return (
    <Column spacing="400">
      <Column>
        <Text type="b500">
          Pending Restrictions ({pageState.data.pending.length + pageState.data.transferredIn.length})
        </Text>
        {pageState.data.pending.map((r) => (
          <RestrictionCardLayout key={`${r.accommodationId}-${r.taskName}`} restriction={r} />
        ))}
        {pageState.data.transferredIn.map((r) => (
          <RestrictionCardLayout key={`${r.accommodationId}-${r.taskName}`} restriction={r} />
        ))}
      </Column>
      <Column>
        <Text type="b500">Active Restrictions ({pageState.data.active.length})</Text>
        {pageState.data.active.map((r) => (
          <RestrictionCardLayout key={`${r.accommodationId}-${r.taskName}`} restriction={r} />
        ))}
      </Column>
    </Column>
  );
};

export default AssociateProfileRestrictions;
